import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { CapacityPlannerScreen } from '../../screen/CapacityPlanner/Screen';
import { userColor } from './types';
import { resolveTicketAssigneeId } from './types';

interface Props {
    planner: CapacityPlannerScreen;
}

/**
 * [T54157] Per-developer priority queues for a milestone.
 *
 * Instead of dragging tickets onto timeline dates, planners drag tickets into
 * a developer's queue and order them by priority. The backend computes the
 * concrete scheduled_start/scheduled_end from each developer's availability
 * (allocation entries per weekday/project, minus weekends/holidays), so the
 * timeline shows when each ticket will be finished.
 */
@observer
export class PriorityQueuePanel extends React.Component<Props> {
    render() {
        const p = this.props.planner;
        if (!p.detailViewOpen || !p.viewingMilestoneId || !p.detailProjectId) return null;
        const proj = p.projects.find(pr => pr.id === p.detailProjectId);
        // Priority queues are only available for planning milestones — the
        // schedule_queues endpoint lives on planning.Milestone.
        const milestone = (p.planningMilestones as any[]).find(m => m.id === p.viewingMilestoneId);
        if (!proj || !milestone) return null;

        const tickets = p.getMilestonePlanningTickets(milestone);
        const allocatedDevs = p.getAllocatedDevelopersForProject(proj);
        // [T54157] mobx-spine parses a null FK relation as an EMPTY model instance
        // (assignee is an object with id === null). `t.assignee?.id ?? t.assignee`
        // falls through to that object, so resolve the id explicitly.
        const isUnassigned = (t: any) => resolveTicketAssigneeId(t) === null;
        const unassigned = tickets.filter(isUnassigned);

        return (
            <div className="pq-panel" data-test-cp-pq-panel>
                <div className="pq-header">
                    <h3>Priority Queues</h3>
                    <button
                        className="btn btn-primary btn-sm"
                        data-test-cp-pq-auto-schedule
                        onClick={() => p.saveQueues()}
                        disabled={p.queuesSaving}
                    >
                        {p.queuesSaving ? 'Scheduling…' : 'Schedule queues'}
                    </button>
                </div>
                <p className="pq-hint">
                    Drag tickets into a developer's queue and order them by priority.
                    Dates are computed from each developer's available time.
                </p>

                {/* Unassigned pool */}
                <div
                    className={'pq-queue pq-unassigned' + (p.pqDragOverQueue === 'unassigned' ? ' drag-over' : '')}
                    data-test-cp-pq-unassigned
                    onDragOver={e => { e.preventDefault(); p.setPqDragOver('unassigned'); }}
                    onDragLeave={() => p.setPqDragOver(null)}
                    onDrop={e => p.onPqDrop(e, 'unassigned')}
                >
                    <div className="pq-queue-title">Unassigned ({unassigned.length})</div>
                    {unassigned.map((t: any) => this.renderTicketChip(t))}
                    {unassigned.length === 0 && <div className="pq-empty">All tickets assigned</div>}
                </div>

                {/* One queue per allocated developer */}
                {allocatedDevs.map((dev: any) => {
                    const devTickets = p.getQueuedTicketsForDev(milestone, dev.id);
                    const finish = p.getQueueFinishDate(milestone, dev.id);
                    return (
                        <div
                            key={dev.id}
                            className={'pq-queue' + (p.pqDragOverQueue === dev.id ? ' drag-over' : '')}
                            data-test-cp-pq-queue={dev.id}
                            onDragOver={e => { e.preventDefault(); p.setPqDragOver(dev.id); }}
                            onDragLeave={() => p.setPqDragOver(null)}
                            onDrop={e => p.onPqDrop(e, dev.id)}
                        >
                            <div className="pq-queue-title">
                                <div className="dev-avatar" style={{ background: userColor(dev) }}>
                                    {(dev.fullName || '?').charAt(0)}
                                </div>
                                <span>{dev.fullName}</span>
                                <span className="pq-queue-meta">
                                    {devTickets.length} ticket{devTickets.length === 1 ? '' : 's'}
                                    {finish && ` · finishes ${moment(finish).format('D MMM')}`}
                                </span>
                            </div>
                            {devTickets.map((t: any, idx: number) => this.renderTicketChip(t, idx))}
                            {devTickets.length === 0 && <div className="pq-empty">Drop tickets here</div>}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderTicketChip(t: any, idx?: number) {
        const p = this.props.planner;
        const label = t.title || t.fullName || `T${t.number || t.id}`;
        const status = p.getTicketStatus(t);
        const est = p.getTicketEstimate(t);
        return (
            <div
                key={t.id}
                className={'pq-chip' + (status === 'done' ? ' done' : '') + (idx !== undefined ? ' ranked' : '')}
                data-test-cp-pq-chip={t.id}
                draggable
                onDragStart={e => p.onPqDragStart(e, t.id)}
                onDragEnd={() => p.setPqDragOver(null)}
            >
                {idx !== undefined && <span className="pq-rank">{idx + 1}</span>}
                <span className="pq-chip-name">{label}</span>
                <span className="pq-chip-dates">
                    {t.scheduledEnd && moment(t.scheduledEnd).format('D MMM')}
                </span>
                <span className="chip-hours">{est}h</span>
            </div>
        );
    }
}

export default PriorityQueuePanel;
